/** @jsxImportSource @emotion/react */
import * as yup from "yup";
import { css } from "@emotion/react";
import { FormProvider, useForm } from "react-hook-form";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useCreateBalanceAccount,
  useGetAdyenAccountHolder,
} from "../../service/adyen-account-holders-api";
import { getErrorMessage } from "../../../../error-handler";
import { colors } from "../../../../components/colors";
import {
  ButtonAction,
  ButtonInternalLink,
} from "../../../../components/NewButtons";
import { ActionSpinner } from "../../../../components/ActionSpinner";
import { SpinnerBlock } from "../../../common/Spinner";
import { TextAreaInput } from "../../../../components/forms/inputs/TextAreaInput";
import { AdyenBalanceAccountFormType } from "../../models/adyen-balance-account";
import { getCountrySpecificValues } from "../../../../util";
import { useGetCountries } from "../../../../service/api";
import { useEffect } from "react";
import { SelectInput } from "../../../../components/forms/inputs/SelectInput";

export const buttonWrapperStyle = css`
  position: sticky;
  bottom: 0;
  z-index: 100;
  background-color: ${colors.white};
  display: flex;
  flex-direction: row;
  border-radius: 0px 0px 8px 8px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.greyBorder};
  gap: 8px;
  width: 100%;
  align-items: center;
  height: fit-content;
  margin-top: auto;
  justify-content: end;
`;

export const formStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: auto;
`;

export const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 24px;
  padding-bottom: 24px;
  width: 600px;
`;

export const bodySectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const bodyHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const schema = yup.object({
  description: yup.string().required("Description is required"),
  currency: yup.string().required("Currency is required"),
});

export const MutateBalanceAccountForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currency } = getCountrySpecificValues();

  const methods = useForm<AdyenBalanceAccountFormType>({
    defaultValues: {
      description: "",
      currency,
    },
    resolver: yupResolver(schema),
  });

  const { reset } = methods;

  const {
    data: accountHolder,
    isLoading: isLoadingAccountHolder,
  } = useGetAdyenAccountHolder(id);
  const { data: countries, isLoading: isLoadingCountries } = useGetCountries();

  const { mutateAsync: createBalanceAccount } = useCreateBalanceAccount();

  const onSubmit = async (data: AdyenBalanceAccountFormType) => {
    if (!id) {
      return;
    }

    try {
      await createBalanceAccount({
        data: {
          accountHolderId: id,
          ...data,
        },
      });
      toastr.success("Success", `Adyen balance account created successfully`);
      navigate("..");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  useEffect(() => {
    if (
      isLoadingAccountHolder ||
      isLoadingCountries ||
      !countries ||
      !accountHolder
    ) {
      return;
    }
    const country = countries.find(
      (c) => c.countryCode === accountHolder.countryCode
    );

    if (country) {
      reset({
        description: "",
        currency: country.currency,
      });
    }
  }, [
    reset,
    accountHolder,
    isLoadingAccountHolder,
    isLoadingCountries,
    countries,
  ]);

  if (isLoadingAccountHolder || isLoadingCountries) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...methods}>
      <form css={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
        <div css={bodyStyle}>
          <div css={bodySectionStyle}>
            <TextAreaInput
              name="description"
              label="Description"
              placeholder="Enter the description"
            />
            <SelectInput
              name="currency"
              label="Currency"
              options={Array.from(
                new Set(
                  (countries || [])
                    .filter((c) => c.enabled)
                    .map((c) => c.currency)
                )
              ).map((c) => ({
                value: c,
                label: c,
              }))}
            />
          </div>
        </div>
        <div css={buttonWrapperStyle}>
          <ButtonInternalLink to={".."}>Cancel</ButtonInternalLink>
          <ButtonAction type="submit" disabled={methods.formState.isSubmitting}>
            {methods.formState.isSubmitting ? (
              <ActionSpinner />
            ) : (
              "Create balance account"
            )}
          </ButtonAction>
        </div>
      </form>
    </FormProvider>
  );
};
